import React from 'react';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { SecondaryBody } from '@grnhse/seedling/lib/birch/typography';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import { TertiaryButton } from '@grnhse/seedling/lib/birch/components/buttons';
import { HelpTooltip } from '@grnhse/seedling/lib/birch/components/tooltips';
import PopoverButton from 'talent_matching/components/filters/popover_button';
import { withLocalePrefix } from 'shared/utils/translation';
import { useUserContext } from 'talent_matching/contexts/user_context';
import ApplicationDate from 'talent_matching/components/filters/application_date';
import { OtherActiveApplicationsContent } from 'talent_matching/components/filters/other_active_applications';
import { PreviouslyRejectedContent } from 'talent_matching/components/filters/previously_rejected';
import { LocationContent } from 'talent_matching/components/filters/location';
import { FeedbackStatusContent } from 'talent_matching/components/filters/feedback_status';
import QuestionFiltersSection from 'talent_matching/components/filters/question_filters';
import { SCORECARD_STATUS } from 'talent_search/shared/types';
import AdvancedFilter from 'talent_matching/components/filters/advanced_filter';

const t = withLocalePrefix('talent_matching.filter_row');

type FilterSectionProps = {
  title: string;
  children: React.ReactNode;
  titleAdornment?: React.ReactNode;
};

const FilterSection = ({
  title,
  children,
  titleAdornment,
}: FilterSectionProps) => {
  return (
    <Stack spacing={1}>
      <Stack spacing={1} direction="row" alignItems="center">
        <SecondaryBody medium>{title}</SecondaryBody>
        {titleAdornment}
      </Stack>
      {children}
    </Stack>
  );
};

const ApplicationFiltersGroup = () => {
  const { canUseLocations, locationFilterReadOnly } = useUserContext();
  const { watch, setValue } = useFormContext();
  const [
    appliedOnStart,
    appliedOnEnd,
    appliedOnType,
    locationFilterType,
    debouncedLocationName,
    geolocation,
    otherActiveApplications,
    excludePreviouslyRejected,
    feedbackStatus,
    questionFilters,
  ] = watch([
    'appliedOnStart',
    'appliedOnEnd',
    'appliedOnType',
    'locationFilterType',
    'debouncedLocationName',
    'geolocation',
    'otherActiveApplications',
    'excludePreviouslyRejected',
    'feedbackStatus',
    'questionFilters',
  ]);

  const hasApplicationDate = Boolean(
    appliedOnType && (appliedOnStart || appliedOnEnd)
  );
  const hasOtherActiveApplications = Boolean(otherActiveApplications);
  const hasPreviouslyRejected = Boolean(excludePreviouslyRejected);
  const hasLocation =
    canUseLocations &&
    ((locationFilterType === 'radius' && geolocation) ||
      (locationFilterType === 'name' && debouncedLocationName));
  const hasFeedbackStatus = feedbackStatus !== SCORECARD_STATUS.ALL;
  const appliedQuestionFilters = (questionFilters as AdvancedFilter[]) || [];
  const hasQuestionFilters = appliedQuestionFilters.some(
    (filter: AdvancedFilter) => filter.applied()
  );

  const clear = () => {
    setValue('appliedOnType', null, { shouldDirty: true });
    setValue('appliedOnStart', null, { shouldDirty: true });
    setValue('appliedOnEnd', null, { shouldDirty: true });
    setValue('excludePreviouslyRejected', false, { shouldDirty: true });
    setValue('otherActiveApplications', null, { shouldDirty: true });
    setValue('feedbackStatus', SCORECARD_STATUS.ALL, { shouldDirty: true });
    setValue('questionFilters', [], { shouldDirty: true });
    if (!locationFilterReadOnly) {
      setValue('geolocation', null, { shouldDirty: true });
      setValue('geolocationRadius', '5', { shouldDirty: true });
      setValue('locationFilterType', 'radius', { shouldDirty: true });
      setValue('locationName', '', { shouldDirty: true });
      setValue('debouncedLocationName', '', { shouldDirty: true });
    }
  };
  const appliedCount = [
    hasApplicationDate,
    hasOtherActiveApplications,
    hasPreviouslyRejected,
    hasLocation,
    hasFeedbackStatus,
    hasQuestionFilters,
  ].filter(Boolean).length;

  const buttonLabel = `${t('grouped_buttons.application')}${
    appliedCount ? ` (${appliedCount})` : ''
  }`;

  return (
    <PopoverButton
      buttonLabel={buttonLabel}
      title={t('grouped_buttons.application_filters')}
      width="700px"
    >
      <Stack spacing={3}>
        <FilterSection title={t('application_date.title')}>
          <ApplicationDate />
        </FilterSection>
        {canUseLocations && (
          <FilterSection
            title={t('location.title')}
            titleAdornment={
              <HelpTooltip
                label={t('location.application_tooltip')}
                ariaLabel={t('location.application_tooltip')}
              />
            }
          >
            <LocationContent readOnly={locationFilterReadOnly} />
          </FilterSection>
        )}
        <FilterSection title={t('previously_rejected.title')}>
          <PreviouslyRejectedContent />
        </FilterSection>
        <FilterSection title={t('other_active_applications.title')}>
          <OtherActiveApplicationsContent />
        </FilterSection>
        <FilterSection title={t('feedback_status.title')}>
          <FeedbackStatusContent />
        </FilterSection>
        <QuestionFiltersSection />
        <Stack sx={{ alignSelf: 'flex-start' }}>
          <TertiaryButton onClick={clear} size="lg">
            {t('grouped_buttons.clear')}
          </TertiaryButton>
        </Stack>
      </Stack>
    </PopoverButton>
  );
};

export default ApplicationFiltersGroup;
